import { Skeleton } from 'tailwind-v4/components/skeleton'

/**
 * Loading skeleton shaped like the List View roster — a header bar, a stack
 * of flat time-ordered rows (status edge-bar · time · type dot · name ·
 * table · covers, with a sparse attention badge), a NOW divider, and a
 * collapsed "Completed" group pinned at the bottom — so the wait reads as
 * "the roster is coming" rather than a generic loading page.
 *
 * Deliberately dependency-free beyond the v4 `Skeleton`: it is used BOTH as
 * the route-level Suspense fallback (imported statically by paths.tsx) AND as
 * the page's first-paint state, so it must NOT import the `blocks/booking-list`
 * block, the page, or anything that drags the list-view-3 chunk into the main
 * bundle. Mirrors day-planner-fast's `DayPlannerSkeleton` (used the same two
 * ways).
 *
 * Fills the whole height: it renders more rows than any realistic screen
 * shows and clips the overflow (`flex-1` + `overflow-hidden`), so the surface
 * is full top-to-bottom with the Completed bar always at the foot.
 */

// Deterministic roster rows — hand-derived name widths + a sparse set of
// attention badges + one NOW divider, so it reads like a believable service
// rather than random noise.
const ROSTER_ROWS = Array.from({ length: 16 }, (_, i) => ({
  id: `row-${String(i)}`,
  name: `${String(38 + ((i * 13) % 46))}%`, // 38–84%
  hasBadge: (i * 7 + 2) % 6 === 0, // ~1 row in 6 carries an attention badge
  nowDivider: i === 5, // NOW sits ~a third of the way down
}))

export function ListViewSkeleton() {
  return (
    <div
      aria-busy="true"
      aria-label="Loading bookings"
      className="flex h-full min-h-0 flex-col bg-background"
    >
      {/* Header — date group left, action cluster + Create pill right
          (mirrors the shared BookingsHeader shape) */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <Skeleton className="h-9 w-40 rounded-lg md:w-72" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="hidden size-8 rounded-lg md:block" />
          <Skeleton className="size-8 rounded-lg" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
      </div>

      {/* Roster — a centred column of flat rows; render more than fit and clip
          the overflow so it's full-height (never leaving blank space below) */}
      <div className="min-h-0 flex-1 overflow-hidden p-4">
        <div className="flex w-full flex-col">
          {ROSTER_ROWS.map((row) => (
            <div key={row.id}>
              {row.nowDivider ? (
                <div className="flex items-center gap-3 py-2">
                  <Skeleton className="h-5 w-24 rounded-full" />
                  <div className="h-px flex-1 bg-border" aria-hidden="true" />
                </div>
              ) : null}
              <div className="flex h-12 items-center gap-3 border-b border-border/60">
                {/* status edge-bar */}
                <Skeleton className="h-6 w-1 shrink-0 rounded-full" />
                {/* time */}
                <Skeleton className="h-3 w-12 shrink-0 rounded" />
                {/* tiny booking-type dot */}
                <Skeleton className="size-2 shrink-0 rounded-full" />
                {/* guest name */}
                <div className="min-w-0 flex-1">
                  <Skeleton
                    className="h-3.5 rounded"
                    style={{ width: row.name }}
                  />
                </div>
                {/* table · covers · optional attention badge */}
                <div className="flex shrink-0 items-center gap-3">
                  <Skeleton className="h-3 w-8 rounded" />
                  <Skeleton className="h-3 w-6 rounded" />
                  {row.hasBadge ? (
                    <Skeleton className="h-5 w-16 rounded-full" />
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Collapsed "Completed (N)" group pinned at the foot of the roster */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        <div className="flex w-full items-center gap-2">
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-3.5 w-28 rounded" />
          <Skeleton className="ml-auto h-5 w-8 shrink-0 rounded-full" />
        </div>
      </div>
    </div>
  )
}

export default ListViewSkeleton
